import { useState } from 'react';
import { Lock, FileCode, Network, Database, Terminal, Info, CheckCircle2 } from 'lucide-react';
import RiskBadge from './RiskBadge';
import ToolDetailDialog, { ToolDetail } from './ToolDetailDialog';
import { cn } from '@/lib/utils';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface Permission {
  capability: string;
  riskLevel: RiskLevel;
  tools: string[];
  icon: 'code' | 'network' | 'database' | 'terminal' | 'file';
  riskJustification: string;
}

interface PermissionScopeProps {
  permissions: Permission[];
  toolDetails?: Record<string, ToolDetail>;
}

const iconMap = {
  code: FileCode,
  network: Network,
  database: Database,
  terminal: Terminal,
  file: Lock,
};

const PermissionScope = ({ permissions, toolDetails = {} }: PermissionScopeProps) => {
  const [selectedTool, setSelectedTool] = useState<ToolDetail | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleToolClick = (toolName: string) => {
    const detail = toolDetails[toolName];
    if (detail) {
      setSelectedTool(detail);
      setDialogOpen(true);
    }
  };

  if (permissions.length === 0) {
    return (
      <div className="card-elevated p-8 animate-slide-up flex flex-col items-center gap-3 text-center">
        <CheckCircle2 className="w-10 h-10 text-green-500 opacity-80" />
        <p className="font-semibold text-sm">No permission concerns detected</p>
        <p className="text-xs text-muted-foreground max-w-sm">
          No tool abuse, data exfiltration, injection, or access-control findings were identified in
          this scan.
        </p>
      </div>
    );
  }

  const totalTools = permissions.reduce((acc, p) => acc + p.tools.length, 0);

  return (
    <>
      <div className="card-elevated p-6 animate-slide-up">
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-gradient-to-r from-risk-high to-risk-medium p-2 rounded-lg">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-semibold">Permission Scope Analysis</h3>
          <div className="ml-auto bg-muted px-3 py-1 rounded-full text-sm">
            <span className="text-muted-foreground">Total Tools: </span>
            <span className="font-bold">{totalTools}</span>
          </div>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Click on any tool to learn more about its capabilities and risk assessment
        </p>

        <div className="overflow-x-auto">
          <table className="table-modern">
            <thead>
              <tr>
                <th>Capability</th>
                <th>Risk Level</th>
                <th>Tools / Functions</th>
                <th>Risk Justification</th>
              </tr>
            </thead>
            <tbody>
              {permissions.map((permission, index) => {
                const Icon = iconMap[permission.icon];
                return (
                  <tr key={index}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-muted rounded-lg">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <span className="font-medium">{permission.capability}</span>
                      </div>
                    </td>
                    <td>
                      <RiskBadge level={permission.riskLevel} />
                    </td>
                    <td>
                      <div className="flex flex-wrap gap-1">
                        {permission.tools.map((tool, i) => {
                          const hasDetails = !!toolDetails[tool];
                          return (
                            <code
                              key={i}
                              className={cn(
                                'text-xs bg-muted px-2 py-1 rounded font-mono transition-all inline-flex items-center gap-1',
                                hasDetails &&
                                  'cursor-pointer hover:bg-primary hover:text-primary-foreground border border-transparent hover:border-primary/30'
                              )}
                              onClick={() => hasDetails && handleToolClick(tool)}
                              role={hasDetails ? 'button' : undefined}
                              tabIndex={hasDetails ? 0 : undefined}
                              onKeyDown={
                                hasDetails
                                  ? (e) => e.key === 'Enter' && handleToolClick(tool)
                                  : undefined
                              }
                            >
                              {tool}
                              {hasDetails && <Info className="w-3 h-3 shrink-0 opacity-50" />}
                            </code>
                          );
                        })}
                      </div>
                    </td>
                    <td>
                      <p className="text-sm text-muted-foreground max-w-xs">
                        {permission.riskJustification}
                      </p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ToolDetailDialog tool={selectedTool} open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
};

export default PermissionScope;
